import {
  ProductLoader,
  generateSrcset,
  matchesMediaQuery,
  formatMoney
} from 'theme'
import {
  buildBadgeHtml,
  buildSaveMessageHtml,
} from '../scripts/product-card-renderers'
import { GWElement } from 'elements'
import { getSwatchProducts, renderSwatches } from 'swatches'
import { addVariantToCart, parseAttribute, parseGID, parseMetafieldValue, imageSourcesMatch } from 'utilities'
import { inView, animate, stagger } from 'vendor'
import { roundCentlessPrices } from 'mondetta'

/**
 * Product card element modified for use with split product variants and size selection for direct quick-add functionality.
 * 
 * @extends GWElement
 */
export class VariantProductCard extends GWElement {
  constructor() {
    super()

    this.swatches = this.querySelector('[data-swatches]')
    this.sizes = this.querySelector('[data-sizes]')
    this.form = this.querySelector('form[is="product-form"]')
    const quickAddButton = this.querySelector('.product-card__quick-add-button')
    const sizeButtons = [...(this.sizes?.querySelectorAll('[data-size]') ?? [])]
    this.sizeButtons = sizeButtons.length ? sizeButtons.reduce((acc, sizeButton) => ({ ...acc, [sizeButton.dataset.size]: sizeButton }), {}) : false
    this.buttons = [quickAddButton, ...sizeButtons]

    if (this.sizes && this.sizes.querySelector('button[data-size]')) {
      this.addDelegatedListener(this.sizes, 'button[data-size]', 'click', this.onSizeSelected)
    }

    if (!this.swatches) return

    if (this.swatches.querySelectorAll('[type="radio"]').length > 0) {
      // TODO: Update implementation to load all variant products (remove auto-loading on page load)
      this.addEventListener(
        'pointerover',
        () => ProductLoader.load(this.getAttribute('handle')),
        { once: true },
      )
    }

    this.addListener(
      this.swatches,
      'change',
      this.onSwatchChanged,
    )

    this.addAttributeListener('current-color', this.onColorChange)
  }

  init() {
    super.init()

    if (this.swatches) {
      this.initSwatches()
    }
  }

  static get observedAttributes() {
    return ['current-color']
  }

  set handle(value) {
    this.setAttribute('handle', value)
    // Propagate handle updates to the quick-buy-modal element
    this.querySelector('quick-buy-modal')?.setAttribute('handle', value)
  }

  get productTitle() {
    return this.getAttribute('product-title')
  }

  get color() {
    return this.getAttribute('current-color')
  }

  set color(value) {
    this.setAttribute('current-color', value)
  }

  get queryBySale() {
    return parseAttribute(this.getAttribute('query-by-sale'))
  }

  get productTags() {
    if (!this.product?.tags) return [];

    const originalTagArray = this.product.tags;
    let tagArray = [];

    if (Array.isArray(originalTagArray)) {
      originalTagArray.forEach(object => {
        let tagArrayItem = Object.values(object).join("");
        tagArray.push(tagArrayItem);
      });
      return tagArray;
    }
    return [];
  }

  /**
   * Loads the variant products and renders the associated swatches.
   */
  async initSwatches() {
    const { productTitle, queryBySale } = this
    const products = await getSwatchProducts(productTitle.toLowerCase(), { queryBySale })

    this.variantProducts = products.reduce((acc, product) => (product.swatch ? { ...acc, [product.swatch.name]: product } : acc), {})
    this.updateProduct()

    const { swatchIdPrefix: idPrefix, name } = this.swatches.dataset
    this.renderSwatches({
      container: this.swatches,
      products,
      idPrefix,
      name,
      current: this.color,
    })

    this.addListener(
      '.product-swatch input + label',
      'pointerover',
      this.onSwatchHovered,
    )

    this.dispatchEvent(new CustomEvent("products:loaded", { bubbles: true }))
  }

  renderSwatches(data) {
    renderSwatches(data)
  }

  /**
   * Creates an image element with the specified media, className, and sizes.
   * 
   * @param {Object} media - The media object containing width, height, url, and alt attributes.
   * @param {string} className - The class name of the image element.
   * @param {string} sizes - The sizes attribute of the image element.
   * @returns {HTMLImageElement} - The newly created image element.
   */
  createImage(media, className, sizes, data = {}) {
    const image = new Image(media.width, media.height)
    const imageUrl = new URL(media.url)
    const widths = [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800]
    image.setAttribute('class', className)
    image.setAttribute('sizes', sizes)
    image.alt = image.altText || ''
    image.src = imageUrl.href
    image.srcset = generateSrcset({ src: media.url, width: media.width }, widths)

    for (const key in data) {
      image.dataset[key] = data[key]
    }

    // for (const key in attributes) {
    //   if (key === 'data') {
    //     for (const key in attributes[key]) {
    //       image.dataset[key] = attributes[key][key]
    //     }
    //     continue
    //   }

    //   image.setAttribute(key, attributes[key])
    // }

    return image
  }

  get variant() {
    return this.product.variants[0]
  }

  /**
   * Preloads the primary image for a swatch's associated product on hover.
   *
   * @param {Event} event - The swatch's `hover` event.
   */
  async onSwatchHovered(event) {
    const variantProduct = this.variantProducts[event.target.control.value]
    const primaryMediaElement = this.querySelector(
      '[data-card-image="primary"]',
    )
    if (variantProduct.images[0]) {
      this.createImage(variantProduct.images[0], primaryMediaElement.className, primaryMediaElement.sizes, primaryMediaElement.dataset)
    }
  }

  onColorChange(value, previousValue) {
    if (value === previousValue) return

    if (this.product) {
      this.updateProduct()
    } else {
      this.addEventListener('products:loaded', this.updateProduct.bind(this), { once: true })
      // this.addEventListener('products:loaded', () => this.updateProduct(), { once: true })
    }
  }

  /**
   * Sets the variant product based on the specified color.
   *
   * @param {string} color - The color for which to set the variant product.
   */
  async updateProduct() {
    const previousVariantProduct = this.product
    this.product = this.variantProducts[this.color]

    if (previousVariantProduct?.id === this.product?.id) return

    if (this.form) {
      this.form['product-id'].value = parseGID(this.product.id)
      this.form.id.value = parseGID(this.product.variants[0].id)
    }

    this.handle = this.product.handle

    if (this.sizeButtons) {
      this.product.variants.forEach(variant => {
        const id = parseGID(variant.id)
        const size = variant.selectedOptions.find(option => option.name === 'Size').value
        const button = this.sizeButtons[size]
        if (!button) return
        button.setAttribute('data-variant-id', id)
        const disabled = !variant.quantityAvailable
        button.classList.toggle('sold-out', disabled)
        button.toggleAttribute('disabled', disabled)

        if (button.tagName === 'INPUT' && disabled && button.checked) {
          button.checked = false
          button.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })
    }

    const initialProductPath = new URL(this.product.onlineStoreUrl).pathname
    const url = new URL(this.product.onlineStoreUrl)
    url.searchParams.set('variant', this.variant?.id)
    const href = `${url.pathname}${url.search}${url.hash}`
    this.querySelectorAll(`a[href^="${initialProductPath}"`).forEach(link => link.href = href)
    if (!this.product.images.length) return
    const primaryMediaElement = this.querySelector('[data-card-image="primary"]')
    const secondaryMediaElement = this.querySelector('[data-card-image="secondary"]')
    const newPrimaryMediaElement = this.createImage(
      this.product.images[0],
      primaryMediaElement.className,
      primaryMediaElement.sizes,
      primaryMediaElement.dataset,
    )

    if (!imageSourcesMatch(primaryMediaElement, newPrimaryMediaElement)) {
      if (secondaryMediaElement && this.product.images.length > 1) {
        secondaryMediaElement.replaceWith(
          this.createImage(
            this.product.images[1],
            secondaryMediaElement.className,
            secondaryMediaElement.sizes,
            secondaryMediaElement.dataset,
          ),
        )
      }
      await primaryMediaElement.animate(
        { opacity: [1, 0] },
        { duration: 150, easing: 'ease-in', fill: 'forwards' },
      ).finished
      await new Promise(resolve =>
        newPrimaryMediaElement.complete
          ? resolve()
          : (newPrimaryMediaElement.onload = () => resolve()),
      )
      primaryMediaElement.replaceWith(newPrimaryMediaElement)
      newPrimaryMediaElement.animate(
        { opacity: [0, 1] },
        { duration: 150, easing: 'ease-in' },
      )
    }
    /**
     * Remove Pre-order tag from product cards
     * February 11, 2025
     */
    // const hasVariantShippingDate = this.product.variants.some(variant => !!parseMetafieldValue(variant.shippingDate))
    // if (shippingDate || hasVariantShippingDate) {
    //   const preorderSpan = `<span class="preorder-tag">${window.themeVariables.strings.preorder}</span>`
    //   this.insertAdjacentHTML('beforeend', preorderSpan)
    // } else {
    //   this.querySelector('.preorder-tag')?.remove()
    // }

    const shippingDate = this.getShippingDate(this.product)

    for (const sizeButton of this.querySelectorAll('.product-card__sizes button')) {
      const sizeVariant = this.getVariantById(sizeButton.dataset.variantId)
      const variantShippingDate = this.getShippingDate(sizeVariant) ?? shippingDate

      if (variantShippingDate) {
        const preOrderIcon = window.themeVariables.strings.preOrderIcon

        sizeButton?.classList.add('is-preorder')
        sizeButton?.insertAdjacentHTML('beforeend', preOrderIcon)
      } else {
        sizeButton.querySelector('.preorder-icon')?.remove()
      }
    }

    const priceWrapper = this.querySelector('.price-list')
    if (!priceWrapper) return

    if (this.product.onSale) {
      priceWrapper.innerHTML = `
        <compare-at-price class="h5 text-subdued line-through">
          <span class="sr-only">
            Regular price
          </span>`
        + formatMoney(this.product.compareAtPriceRange.minVariantPrice.amount * 100)
        + ` `
        + Shopify.currency.active
        + `</compare-at-price>
        <sale-price class="h5 text-on-sale">
          <span class="sr-only">
            Sale price
          </span>`
        + formatMoney(this.product.priceRange.minVariantPrice.amount * 100)
        + ` `
        + Shopify.currency.active
        + `</sale-price>
      `;
    } else {
      priceWrapper.innerHTML = `
        <sale-price class="h5">
          <span class="sr-only">
            Price
          </span>`
        + formatMoney(this.product.priceRange.minVariantPrice.amount * 100)
        + ` `
        + Shopify.currency.active
        + `</sale-price>
      `;
    }

    // round centless prices
    roundCentlessPrices()

    // Render badges and save-messages
    this.renderBadges()
    this.renderSaveMessages()
  }

  /**
   * renderBadges()
   * 
   * Renders product badges (custom, sold_out, discount) based on product data and settings.
   * Core logic is coming from product-card-renderers.js.
   * 
   * ***MUST*** match logic in product-badges.liquid.
   */
  renderBadges() {
    const badgeList = this.querySelector('badge-list');
    if (!badgeList) return;

    const { settings, strings } = window.themeVariables;
    const html = buildBadgeHtml({
      product: this.product,
      productTags: this.productTags,
      settings,
      strings,
      isB2B: Shopify.customer?.b2b === true,
    })

    badgeList.innerHTML = html;
    badgeList.toggleAttribute('hidden', html.trim() === '');
  }

  /**
   * renderSaveMessages()
   * 
   * Renders save-message elements based on product tags, settings, and metafields.
   * Core logic is coming from product-card-renderers.js.
   * 
   * ***MUST*** match logic in product-card.liquid.
   */
  renderSaveMessages() {
    const container = this.querySelector('[data-save-messages]');
    if (!container) return;

    const { settings, strings } = window.themeVariables;
    const html = buildSaveMessageHtml({
      product: this.product,
      productTags: this.productTags,
      settings,
      strings,
      isB2B: Shopify.customer?.b2b === true,
      userRegion: settings.localizationCountry || Shopify.country,
    })

    container.innerHTML = html;
    container.toggleAttribute('hidden', html.trim() === '');
  }

  getShippingDate(entity, ...fallbacks) {
    const shippingDate = parseMetafieldValue(entity.shippingDate)

    if (!shippingDate && fallbacks.length) {
      const [nextEntity, ...remainingFallbacks] = fallbacks
      return this.getShippingDate(nextEntity, ...remainingFallbacks)
    }

    return shippingDate
  }

  getVariantById(variantId) {
    return this.product.variants.find(({ id }) => parseGID(id) === parseInt(variantId, 10))
  }

  /**
   * Performs required updates when a new swatch has been selected.
   *
   * @param {Event} event - The swatch fieldset's `change` event.
   */
  async onSwatchChanged(event) {
    this.color = event.target.value
  }

  /**
   * Selects the appropriate product variant and adds it to the cart.
   *
   * @param {Event} event - The size button's `click` event.
   */
  async onSizeSelected(event) {
    const { target } = event

    if (target.hasAttribute('disabled') || target.classList.contains('sold-out')) return

    this.buttons.forEach((button) => {
      button.setAttribute("disabled", "disabled")
      button.setAttribute("aria-busy", "true")
    });

    let properties = {}
    const { dataset: { variantId } } = target
    const variant = this.getVariantById(variantId)
    const shippingDate = this.getShippingDate(variant, this.product)

    if (shippingDate) {
      properties = {
        preorder: true,
        'preorder-shipdate': shippingDate,
      }
    }

    await addVariantToCart(variantId, 1, properties)

    this.buttons.forEach((button) => {
      button.removeAttribute("disabled")
      button.removeAttribute("aria-busy")
    });
  }
}

if (!window.customElements.get('variant-product-card')) {
  window.customElements.define('variant-product-card', VariantProductCard)
}


/**
 * A product list component modified for use with variant product cards.
 * Handles revealing the product cards when they enter the viewport.
 */
class VariantProductList extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    if (
      matchesMediaQuery('motion-safe') &&
      this.querySelectorAll('product-card[reveal-on-scroll="true"], variant-product-card[reveal-on-scroll="true"]').length > 0
    ) {
      inView(this, this.reveal.bind(this))
    }
  }

  reveal() {
    animate(
      this.querySelectorAll('product-card[reveal-on-scroll="true"], variant-product-card[reveal-on-scroll="true"]'),
      {
        opacity: [0, 1],
        transform: ['translateY(20px)', 'translateY(0)'],
      },
      {
        duration: 0.2,
        easing: 'ease-in-out',
        delay: stagger(0.05, { start: 0.4, easing: 'ease-out' }),
      },
    )
  }
}

if (!window.customElements.get('variant-product-list')) {
  window.customElements.define('variant-product-list', VariantProductList)
}